/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { action, computed, observable } from 'mobx';
import { UIConfigsStore } from 'src/core/stores/UIConfigsStore';
import { FileType } from 'src/core/types/FileType';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { QnaMakerFilterStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';
import { QnaMakerQnaPairsBeingEditedStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsBeingEditedStore';
import { QnaMakerQnaPairInternalRef, QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaMakerContextTreeNode } from 'src/qnaMaker/core/types/QnaMakerContextTreeNode';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';
import { qnaMakerQnaSwaggerToQnaStore } from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';
import { getQnaIdToTreeNodesMap } from 'src/qnaMaker/modules/utils/contextTreeProcessing';
import { getNestedDirtyPairs } from 'src/qnaMaker/modules/utils/dirtyQnaPairsProcessing';

export type SavingState = 'dirty' | 'saved' | 'inProgress';
export type DeletionOperations = 'qna' | 'metadata' | 'followUpPrompts';
export type OptionalColumnKey = 'context' | 'metadata';
export type OptionalColumnVisibility = { key: OptionalColumnKey; isVisible: boolean };

const emptyPair: Partial<QnaMakerQnaSwagger> = {
    id: getRandomNumber(),
    answer: '',
    source: 'Editorial',
    questions: [],
    metadata: {},
    parentQnaIds: [],
    activeLearningSuggestions: [],
    dialog: { isContextOnly: false, prompts: [] }
};
export class QnaMakerQnaPairsUiStore {
    private _portalUiStore: UIConfigsStore;

    public dirtyQnaPairs: QnaPatchOperation<QnaMakerQnaSwagger>[];

    public filtersStores: QnaMakerFilterStore;

    public qnaPairsBeingEditedStore: QnaMakerQnaPairsBeingEditedStore;

    @observable.shallow public qnaPairsJson: QnaMakerQnaSwagger[];

    @observable.shallow public fetchedQnaPairsJson: QnaMakerQnaSwagger[];

    @observable public isSmartSuggestionsMessageDisplayed: boolean;

    @observable public displayedQnaPairStores: QnaMakerQnaStore[];

    @observable public qnaPairToEditId: number;

    @observable public savingState: SavingState;

    @observable public searchQuery: string;

    @observable public isAddQnaPairOn: boolean;

    @observable public isTestPaneOpen: boolean;

    @observable public isFilterBladeOpen: boolean;

    @observable public isRichTextEnabled: boolean;

    @observable public selectedQnaId: number;

    @observable public importProjectModalSettings: { hidden: boolean; fileType: Extract<FileType, 'xlsx' | 'tsv'> };

    @observable public deleteModalSettings: { hidden: boolean; operation: DeletionOperations };

    @observable.shallow public contextTreeMap: Map<number, QnaMakerContextTreeNode[]>;

    @observable public isContextTreeCalculating: boolean;

    @observable public detailsQnaPairId: number;

    constructor(portalUiStore: UIConfigsStore, _qnaPairsJson: QnaMakerQnaSwagger[] = [], sourceStores: QnaMakerSourceStore[] = []) {
        this.filtersStores = new QnaMakerFilterStore(_qnaPairsJson, sourceStores);
        this.dirtyQnaPairs = [];
        this.savingState = 'saved';
        this.searchQuery = '';
        this.displayedQnaPairStores = [];
        this.qnaPairsJson = _qnaPairsJson ?? [];
        this.isAddQnaPairOn = false;
        this.isTestPaneOpen = false;
        this.isFilterBladeOpen = false;
        this.isRichTextEnabled = false;
        this.selectedQnaId = null;
        this.importProjectModalSettings = { hidden: true, fileType: null };
        this.deleteModalSettings = { hidden: true, operation: null };
        this.isContextTreeCalculating = false;
        this.qnaPairsBeingEditedStore = new QnaMakerQnaPairsBeingEditedStore();
        this.isSmartSuggestionsMessageDisplayed = false;
        this._portalUiStore = portalUiStore;
    }

    @computed
    public get filteredDirtyQnaPairs() {
        const nestedPairs = getNestedDirtyPairs(this.dirtyQnaPairs);

        return nestedPairs.map(dirtyPair => {
            // To be removed once the backend fixes this issue on their side
            if (dirtyPair.op === 'add') {
                return {
                    op: dirtyPair.op,
                    value: {
                        ...dirtyPair.value,
                        id: undefined,
                        dialog: {
                            ...dirtyPair.value.dialog,
                            prompts: [...dirtyPair.value.dialog.prompts.map(p => ({ ...p, qnaId: p.qna ? undefined : p.qnaId }))]
                        }
                    }
                };
            }

            return {
                op: dirtyPair.op,
                value: {
                    ...dirtyPair.value,
                    dialog: {
                        ...dirtyPair.value.dialog,
                        prompts: [...dirtyPair.value.dialog.prompts.map(p => ({ ...p, qnaId: p.qna ? undefined : p.qnaId }))]
                    }
                }
            };
        });
    }

    @computed
    public get firstQnaWithAlternateQsId() {
        return this.displayedQnaPairStores.find(qnaPair => qnaPair.questions.length > 1)?.id;
    }

    @action
    public constructContextTreeMap() {
        this.contextTreeMap = getQnaIdToTreeNodesMap(this.qnaPairsJson);
        this.isContextTreeCalculating = false;
    }

    @action
    public setIsSmartSuggestionsMessageDisplayed(isDisplayed: boolean) {
        this.isSmartSuggestionsMessageDisplayed = isDisplayed;
    }

    @action
    public setDirtyQnaPairs(_dirtyQnaPairs: QnaPatchOperation<QnaMakerQnaSwagger>[]) {
        this.dirtyQnaPairs = _dirtyQnaPairs;
    }

    @action
    public setSearchQuery(searchQuery: string) {
        this.searchQuery = searchQuery;
    }

    @action
    public setQnaPairsJson(_qnaPairsJson: QnaMakerQnaSwagger[]) {
        this.qnaPairsJson = [..._qnaPairsJson];
        this.constructContextTreeMap();
    }

    @action
    public setFetchedQnaPairsJson(_fetchedQnaPairsJson: QnaMakerQnaSwagger[]) {
        this.fetchedQnaPairsJson = [..._fetchedQnaPairsJson];
    }

    @action
    public setDisplayedQnaPairStores(_displayedQnaPairStores: QnaMakerQnaStore[]) {
        this.displayedQnaPairStores = _displayedQnaPairStores;
    }

    @action
    public setSelectedQnaId(id: number) {
        this.selectedQnaId = id;
    }

    @action
    public setSavingState(state: SavingState) {
        this.savingState = state;
    }

    @action
    public setIsTestPaneOpen(isOpen: boolean) {
        this.isTestPaneOpen = isOpen;
    }

    @action
    public setIsAddQnaPairOn(isAddQnaPairOn: boolean) {
        this.isAddQnaPairOn = isAddQnaPairOn;
    }

    @action
    public setDetailsQnaPairId(detailsQnaPairId: number) {
        this.detailsQnaPairId = detailsQnaPairId;
    }

    @action
    public toggleIsFilterBladeOpen() {
        this.isFilterBladeOpen = !this.isFilterBladeOpen;
    }

    @action
    public toggleIsRichTextEnabled() {
        this.isRichTextEnabled = !this.isRichTextEnabled;
    }

    @action
    public setDeleteModalSettings(settings: Partial<{ hidden: boolean; operation: DeletionOperations }>) {
        this.deleteModalSettings = { ...this.deleteModalSettings, ...settings };
    }

    @action
    public setImportProjectModalSettings(settings: Partial<{ hidden: boolean; fileType: Extract<FileType, 'xlsx' | 'tsv'> }>) {
        this.importProjectModalSettings = { ...this.importProjectModalSettings, ...settings };
    }

    @action
    public deleteDisplayedQnaPairs(ids: number[]) {
        const parentIdsToBeRemoved = flatten(ids.map(id => this.qnaPairsJson.find(p => p.id === id).dialog.prompts.map(p => p.qnaId)));

        if (
            parentIdsToBeRemoved.length &&
            this._portalUiStore?.qnaMakerEditKbOptionalColumnsVisibility.find(c => c.key === 'context').isVisible
        ) {
            this.isContextTreeCalculating = true;
            setTimeout(() => {
                this.constructContextTreeMap();
            }, 0);
        }

        this.qnaPairsJson = this.qnaPairsJson
            .map(qna => {
                if (ids.includes(qna.id)) {
                    const alreadyAdded = this.dirtyQnaPairs.find(p => p.value.id === qna.id);
                    if (alreadyAdded) {
                        this.dirtyQnaPairs = this.dirtyQnaPairs.filter(p => p.value.id !== qna.id);
                    } else {
                        this.dirtyQnaPairs = [...this.dirtyQnaPairs.filter(p => p.value.id !== qna.id), { op: 'delete', value: qna }];
                    }

                    return null;
                }

                return {
                    ...qna,
                    parentQnaIds:
                        qna.parentQnaIds?.filter(pid => !ids.includes(pid)).filter(pid => !parentIdsToBeRemoved.includes(pid)) ?? [],
                    dialog: { ...qna.dialog, prompts: qna.dialog.prompts.filter(p => !ids.includes(p.qnaId)) }
                };
            })
            .filter(Boolean);

        this.displayedQnaPairStores = this.displayedQnaPairStores
            .filter(qna => !ids.includes(qna.id))
            .map(p => qnaMakerQnaSwaggerToQnaStore(this.qnaPairsJson.find(q => q.id === p.id)));
    }

    @action
    public addDisplayedQnaPairs(qnaPairData: QnaMakerQnaSwagger) {
        const qnaPairToAdd: QnaMakerQnaSwagger = {
            ...emptyPair,
            ...qnaPairData,
            internalRef: QnaMakerQnaPairInternalRef.uiGlobalPair
        };

        if (
            qnaPairData.dialog.prompts.length &&
            this._portalUiStore?.qnaMakerEditKbOptionalColumnsVisibility.find(c => c.key === 'context').isVisible
        ) {
            this.isContextTreeCalculating = true;
            setTimeout(() => {
                this.constructContextTreeMap();
            }, 0);
        }
        this.qnaPairsJson = [qnaPairToAdd, ...this.qnaPairsJson];
        this.displayedQnaPairStores = [qnaMakerQnaSwaggerToQnaStore(qnaPairToAdd), ...this.displayedQnaPairStores];
        this.dirtyQnaPairs = [{ op: 'add', value: qnaPairToAdd }, ...this.dirtyQnaPairs];
    }

    @action
    public editDisplayedQnaPair(qnaId: number, newData: Partial<QnaMakerQnaSwagger>) {
        let updatedQnaPair: QnaMakerQnaSwagger;

        if (
            newData.dialog?.prompts.length &&
            this._portalUiStore?.qnaMakerEditKbOptionalColumnsVisibility.find(c => c.key === 'context').isVisible
        ) {
            this.isContextTreeCalculating = true;
            setTimeout(() => {
                this.constructContextTreeMap();
            }, 0);
        }

        // On adding new prompt point to new qna pair
        newData.dialog?.prompts.forEach(prompt => {
            if (prompt.qna && !this.qnaPairsJson.some(qna => qna.id === prompt.qna.id)) {
                const pairToAdd = <QnaMakerQnaSwagger>{ ...emptyPair, ...prompt.qna };
                this.qnaPairsJson = [pairToAdd, ...this.qnaPairsJson];
                this.displayedQnaPairStores = [qnaMakerQnaSwaggerToQnaStore(pairToAdd), ...this.displayedQnaPairStores];
            }
        });

        this.qnaPairsJson = this.qnaPairsJson.map(qna => {
            if (qna.id === qnaId) {
                updatedQnaPair = { ...qna, ...newData, dialog: { ...qna.dialog, ...newData.dialog } };

                return updatedQnaPair;
            }

            return qna;
        });

        const targetInd = this.displayedQnaPairStores.findIndex(v => v.id === qnaId);
        this.displayedQnaPairStores[targetInd] = qnaMakerQnaSwaggerToQnaStore(this.qnaPairsJson.find(q => q.id === qnaId));

        const alreadyDirty = this.dirtyQnaPairs.find(qna => qna.value.id === qnaId);
        if (alreadyDirty) {
            this.dirtyQnaPairs = this.dirtyQnaPairs.map(dirtyQna => {
                if (dirtyQna.value.id === qnaId) {
                    switch (dirtyQna.op) {
                        case 'add':
                            return { op: 'add', value: updatedQnaPair };
                        case 'replace':
                            return { op: 'replace', value: updatedQnaPair };
                        default:
                    }
                }

                return dirtyQna;
            });
        } else {
            this.dirtyQnaPairs.push({ op: 'replace', value: updatedQnaPair });
        }
    }

    @action
    public resetQnaParis() {
        this.qnaPairsJson = [...this.fetchedQnaPairsJson];

        this.dirtyQnaPairs = [];

        this.displayedQnaPairStores = this.qnaPairsJson.map(p => qnaMakerQnaSwaggerToQnaStore(p));

        this.savingState = 'saved';
    }
}
